var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { MultiColumnFormFields } from "@components/common/form/FormFields";
import { PeekButton, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { useAnalytics, useError, useLoader, useNotification, useRefetch, useUpdateSpace, } from "@piiqtechnologies/ui-lib";
import { Form, Formik } from "formik";
import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import messages from "./messages.i18n";
import { getSpaceSettingsFormFields, SpaceSettingsFormInitialValue, SpaceSettingsFormValidationSchema, } from "./SpaceSettingsConfig";
export const SpaceSettings = ({ spaceDetails }) => {
    const SettingsFormFields = getSpaceSettingsFormFields(spaceDetails);
    const [updateSpaceDetails, spaceDetailsUpdated, updateSpaceDetailsInProgress, updateSpaceDetailsError] = useUpdateSpace();
    const theme = useTheme();
    const { setNotification } = useNotification();
    const { setError } = useError();
    const { setLoader } = useLoader();
    const { setRefetch } = useRefetch();
    const intl = useIntl();
    const analytics = useAnalytics();
    const handleSpaceSettingsUpdate = (_a) => {
        var { grossRent, displayPrice, building: { address } } = _a, values = __rest(_a, ["grossRent", "displayPrice", "building"]);
        let spacePayload = Object.assign(Object.assign({}, values), { _id: spaceDetails._id, floorPlan: { name: values.floorPlan.name }, building: {
                address: ((address === null || address === void 0 ? void 0 : address.name) && {
                    longitude: address === null || address === void 0 ? void 0 : address.longitude,
                    latitude: address === null || address === void 0 ? void 0 : address.latitude,
                    placeId: address === null || address === void 0 ? void 0 : address.placeId,
                    postalCode: address === null || address === void 0 ? void 0 : address.postalCode,
                    city: address === null || address === void 0 ? void 0 : address.city,
                    state: address === null || address === void 0 ? void 0 : address.state,
                    country: address === null || address === void 0 ? void 0 : address.country,
                    geocodeData: address === null || address === void 0 ? void 0 : address.geocodeData,
                    street: (address === null || address === void 0 ? void 0 : address.street) < (address === null || address === void 0 ? void 0 : address.name) ? address === null || address === void 0 ? void 0 : address.street : address === null || address === void 0 ? void 0 : address.name,
                    street2: (address === null || address === void 0 ? void 0 : address.description) || (address === null || address === void 0 ? void 0 : address.street2),
                }) ||
                    undefined,
            } });
        if (!spaceDetails.dxUpdate ||
            !spaceDetails.community.displayPriceField ||
            spaceDetails.community.displayPriceField === "rentPrices.12.price") {
            const existingRentPrices = spaceDetails.rentPrices ? spaceDetails.rentPrices : [];
            const newRentPrice = {
                termInMonths: 12,
                price: typeof displayPrice === "string" ? Number(displayPrice === null || displayPrice === void 0 ? void 0 : displayPrice.replace(/,/g, "")) : displayPrice || 0,
                enabled: true,
            };
            const updatedRentPrices = [
                newRentPrice,
                ...existingRentPrices.filter((rentPrice) => rentPrice.termInMonths !== 12),
            ];
            spacePayload = Object.assign(Object.assign({}, spacePayload), { 
                // @ts-ignore
                rentPrices: updatedRentPrices });
        }
        Object.keys(spacePayload).forEach((key) => {
            //@ts-ignore
            if (spacePayload[key] === null || spacePayload[key] === undefined)
                delete spacePayload[key];
        });
        updateSpaceDetails(spacePayload);
    };
    useEffect(() => {
        setLoader(updateSpaceDetailsInProgress);
        return () => setLoader(false);
    }, [updateSpaceDetailsInProgress]);
    useEffect(() => {
        if (updateSpaceDetailsError) {
            setError(updateSpaceDetailsError === null || updateSpaceDetailsError === void 0 ? void 0 : updateSpaceDetailsError.message);
        }
    }, [updateSpaceDetailsError]);
    useEffect(() => {
        if (spaceDetailsUpdated) {
            setNotification(intl.formatMessage(messages.updateSpaceSettingsSuccess));
            analytics.track("Space Details Updated");
            setRefetch();
        }
    }, [spaceDetailsUpdated]);
    const spaceSettingsInitialValues = useMemo(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        if (!spaceDetails) {
            return SpaceSettingsFormInitialValue;
        }
        return {
            unit: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.unit,
            isModelUnit: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.isModelUnit,
            bedrooms: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.bedrooms,
            bathrooms: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.bathrooms,
            unitSize: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.unitSize,
            description: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.description,
            floorPlan: {
                name: (_a = spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.floorPlan) === null || _a === void 0 ? void 0 : _a.name,
            },
            amenities: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.amenities,
            displayPrice: spaceDetails === null || spaceDetails === void 0 ? void 0 : spaceDetails.displayPrice,
            building: {
                address: (!!((_b = spaceDetails.building) === null || _b === void 0 ? void 0 : _b.address) && Object.assign(Object.assign({}, (_c = spaceDetails.building) === null || _c === void 0 ? void 0 : _c.address), { name: ((_e = (_d = spaceDetails.building) === null || _d === void 0 ? void 0 : _d.address) === null || _e === void 0 ? void 0 : _e.street2) ||
                        [
                            (_g = (_f = spaceDetails.building) === null || _f === void 0 ? void 0 : _f.address) === null || _g === void 0 ? void 0 : _g.street,
                            (_j = (_h = spaceDetails.building) === null || _h === void 0 ? void 0 : _h.address) === null || _j === void 0 ? void 0 : _j.city,
                            (_l = (_k = spaceDetails.building) === null || _k === void 0 ? void 0 : _k.address) === null || _l === void 0 ? void 0 : _l.state,
                            (_o = (_m = spaceDetails.building) === null || _m === void 0 ? void 0 : _m.address) === null || _o === void 0 ? void 0 : _o.country,
                        ]
                            .filter(Boolean)
                            .join(", ") })) ||
                    "",
            },
        };
    }, [spaceDetails]);
    return (React.createElement(PeekStack, { direction: "column", spacing: 1 },
        React.createElement(PeekTypography, { variant: "h3", fontWeight: theme.typography.fontWeightMedium }, intl.formatMessage(messages.updateSpaceTitle)),
        React.createElement(Formik, { initialValues: spaceSettingsInitialValues, validationSchema: SpaceSettingsFormValidationSchema(), enableReinitialize: true, onSubmit: handleSpaceSettingsUpdate },
            React.createElement(Form, null,
                React.createElement(PeekStack, { spacing: 2, pt: { xs: 2.5, md: 0 }, direction: "column", justifyContent: "space-between", height: "100%", sx: { mt: 0 } },
                    React.createElement(MultiColumnFormFields, { fields: SettingsFormFields }),
                    React.createElement(PeekButton, { variant: "contained", size: "large", fullWidth: true, color: "primary", type: "submit" }, intl.formatMessage(messages.update)))))));
};
export default SpaceSettings;
